import { useState, useCallback, RefObject } from "react"

interface UseDragResizeOptions {
  initialHeight?: number
  minHeight?: number
  maxHeight?: number
  containerRef: RefObject<HTMLElement | null>
}

interface UseDragResizeReturn {
  height: number
  setHeight: (height: number) => void
  handleResize: (e: React.MouseEvent) => void
}

/**
 * Hook to handle drag-to-resize functionality for panels.
 * Calculates height as a percentage of the container.
 */
export const useDragResize = ({
  initialHeight = 30,
  minHeight = 10,
  maxHeight = 70,
  containerRef,
}: UseDragResizeOptions): UseDragResizeReturn => {
  const [height, setHeight] = useState<number>(initialHeight)

  const handleResize = useCallback((e: React.MouseEvent): void => {
    e.preventDefault()
    const startY = e.clientY
    const startHeight = height

    const onMouseMove = (moveEvent: MouseEvent): void => {
      const container = containerRef.current?.parentElement
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const deltaY = startY - moveEvent.clientY
      const deltaPercent = (deltaY / containerRect.height) * 100
      const newHeight = Math.min(Math.max(startHeight + deltaPercent, minHeight), maxHeight)
      setHeight(newHeight)
    }

    const onMouseUp = (): void => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
  }, [height, minHeight, maxHeight, containerRef])

  return {
    height,
    setHeight,
    handleResize,
  }
}
